import { useState } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/dae3qa1tto0ceqqngp30";

type FormStatus = "idle" | "loading" | "success" | "error";

export default function SubscribeSection() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // honeypot check
    const honeypot = String(formData.get("email_alt") || "").trim();
    if (honeypot !== "") {
      // simulate success for bots
      setStatus("success");
      setMessage("感谢订阅！热点速报将第一时间送达你的邮箱。");
      form.reset();
      return;
    }

    // remove honeypot from payload
    formData.delete("email_alt");

    setStatus("loading");
    setMessage("");

    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg && String(serverMsg).toLowerCase().includes("spam");

      if (res.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功！热点速报将第一时间送达你的邮箱，记得常来围观哦。");
        form.reset();
      } else {
        const errMsg = isSpam ? "提交过于频繁，请稍后再试。" : serverMsg || "提交失败，请稍后再试。";
        setStatus("error");
        setMessage(errMsg);
      }
    } catch {
      setStatus("error");
      setMessage("网络似乎开小差了，请稍后再试。");
    }
  };

  return (
    <section id="subscribe" className="scroll-mt-32 bg-accent-400">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-xs font-medium text-foreground-700">
              <i className="ri-notification-2-line text-base" aria-hidden="true"></i>
              NEWSLETTER
            </span>
            <h2 className="font-heading mt-2 text-2xl md:text-3xl font-bold text-foreground-950">
              订阅热点速报，吃瓜不迷路
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-800">
              留下邮箱，我们会定期把当日最值得围观的现场直击、榜单变化与深度解读整理成速报发给你，让你在通勤路上、睡前碎片时间里，轻松掌握最新动态。
            </p>
          </div>

          <div className="lg:col-span-6">
            <form
              data-readdy-form="热点速报订阅"
              onSubmit={handleSubmit}
              className="rounded-2xl bg-background-50 p-5 md:p-6"
              noValidate
            >
              <div className="flex flex-col sm:flex-row gap-3">
                <label htmlFor="sub-email" className="sr-only">
                  邮箱地址
                </label>
                <input
                  id="sub-email"
                  type="email"
                  name="email"
                  required
                  placeholder="请输入你的邮箱地址"
                  autoComplete="email"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex shrink-0 whitespace-nowrap items-center justify-center gap-1.5 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                      提交中
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" aria-hidden="true"></i>
                      立即订阅
                    </>
                  )}
                </button>
              </div>

              {/* honeypot */}
              <label htmlFor="alt-email" className="sr-only">确认邮箱</label>
              <input
                id="alt-email"
                type="text"
                name="email_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="alt-email-trap"
              />

              {status === "success" && (
                <p className="mt-3 flex items-center gap-1.5 text-sm text-secondary-700" role="status">
                  <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-3 flex items-center gap-1.5 text-sm text-primary-600" role="alert">
                  <i className="ri-error-warning-line" aria-hidden="true"></i>
                  {message}
                </p>
              )}

              <p className="mt-3 text-[11px] text-foreground-400">
                我们不会向第三方共享你的邮箱，可随时退订。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}